import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Check, 
  Loader2, 
  Star, 
  Zap,
  Shield,
  CreditCard,
  ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: 49,
    priceId: "price_1SxuZKGFQdzo0jB8Nrgu3MYd",
    description: "Pour gérer votre e-réputation",
    features: [
      "QR Code personnalisé",
      "Collecte d'avis illimitée",
      "Tableau de bord & KPIs",
      "Plan d'action basé sur vos avis",
      "Alertes avis négatifs (<4 étoiles)",
      "Support par email"
    ],
    notIncluded: [
      "Réponses IA automatiques",
      "Publication auto sur Google"
    ],
    popular: true,
    icon: Star
  }
  // Premium plan commented - to be used later
  // {
  //   id: "premium",
  //   name: "Premium",
  //   price: 69,
  //   priceId: "price_1SyHk0GFQdzo0jB8UkXrmCzq",
  //   description: "Pour les professionnels",
  //   features: [
  //     "Tout Starter +",
  //     "Réponses IA illimitées",
  //     "Publication auto sur Google",
  //     "KPIs avancés",
  //     "Plan d'action personnalisé",
  //     "Support prioritaire"
  //   ],
  //   popular: true,
  //   icon: Zap
  // }
];

const SelectPlan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUserData } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [loading, setLoading] = useState(false);
  const [trialEligibility, setTrialEligibility] = useState(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate("/signup");
    } else {
      checkTrialEligibility();
    }
  }, [user, navigate]);

  const checkTrialEligibility = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/trial-eligibility`);
      setTrialEligibility(response.data);
    } catch (error) {
      console.error("Error checking trial eligibility:", error);
      // Default to eligible if check fails
      setTrialEligibility({ eligible_for_trial: true, trial_days: 3 });
    }
  };

  const handleSelectPlan = async (planId) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async () => {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;

    setLoading(true);
    try {
      // Create Stripe checkout session with trial (only if eligible)
      const trialDays = trialEligibility?.eligible_for_trial ? 3 : 0;
      
      const response = await axios.post(`${API}/subscriptions/create-checkout`, {
        price_id: plan.priceId,
        plan_name: plan.id,
        trial_days: trialDays,
        success_url: `${window.location.origin}/onboarding?subscription=success`,
        cancel_url: `${window.location.origin}/select-plan?canceled=true`
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-4">
            Choisissez votre forfait
          </h1>
          <p className="text-lg text-slate-600 mb-2">
            {trialEligibility?.eligible_for_trial 
              ? "3 jours d'essai gratuit, sans engagement"
              : "Abonnez-vous pour accéder à toutes les fonctionnalités"
            }
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-emerald-600">
            <Shield className="w-4 h-4" />
            <span>Annulez à tout moment</span>
          </div>
          {trialEligibility?.eligible_for_trial && (
            <p className="text-sm text-slate-500 mt-2">
              <CreditCard className="w-4 h-4 inline mr-1" />
              Carte bancaire requise • Prélèvement de 0€ pendant l'essai
            </p>
          )}
        </div>

        {/* Plans */}
        <div className="flex justify-center mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const Icon = plan.icon;
            
            return (
              <div
                key={plan.id}
                onClick={() => handleSelectPlan(plan.id)}
                data-testid={`plan-card-${plan.id}`}
                className={`relative bg-white rounded-2xl p-8 border-2 cursor-pointer transition-all max-w-md w-full ${
                  isSelected 
                    ? "border-violet-500 shadow-lg shadow-violet-100" 
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-violet-600 to-pink-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Recommandé
                    </span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br from-violet-500 to-pink-500">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500">{plan.description}</p>
                  </div>
                  
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isSelected 
                      ? "border-violet-500 bg-violet-500" 
                      : "border-slate-300"
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                  <span className="text-slate-500">/mois</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Features not included */}
                {plan.notIncluded && plan.notIncluded.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-400 mb-2">Non inclus dans ce forfait :</p>
                    <ul className="space-y-2">
                      {plan.notIncluded.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5 text-center">—</span>
                          <span className="text-slate-400 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">
                Forfait {plans.find(p => p.id === selectedPlan)?.name} - {plans.find(p => p.id === selectedPlan)?.price}€/mois
              </p>
              <p className="text-sm text-slate-500">
                {trialEligibility?.eligible_for_trial 
                  ? "Essai gratuit de 3 jours • Carte bancaire requise • Sans engagement"
                  : "Carte bancaire requise • Sans engagement • Prélèvement immédiat"
                }
              </p>
            </div>
            
            <Button
              onClick={handleSubscribe}
              disabled={loading}
              className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90 px-8"
              data-testid="subscribe-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : trialEligibility?.eligible_for_trial ? (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Commencer l'essai gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  S'abonner maintenant
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>Paiement sécurisé</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            <span>Visa, Mastercard, CB</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>Résiliation en 1 clic</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectPlan;
